import { useEffect, useMemo, useState } from 'react'
import { Event } from '../../../services/EventService'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs'
import { EventCard } from '../../events/components/EventCard'
import { GuestList } from '../../events/components/GuestList'
import { AttendanceManager } from '../../events/components/AttendanceManager'
import { CommunicationPanel } from '../../events/components/CommunicationPanel'
import CoHostsManager from '../../events/components/CoHostsManager'
import { Button } from '../../../components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover'

interface AdminEventManagerProps {
  eventId: string
  asUserId: string
}

export function AdminEventManager({ eventId, asUserId }: AdminEventManagerProps) {
  const [activeTab, setActiveTab] = useState<'guests' | 'cohosts' | 'attendance' | 'communication' | 'sideEvents'>('guests')
  const [eventData, setEventData] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [permissions, setPermissions] = useState<{ canView: boolean; canEdit: boolean; canManageAttendees: boolean; role: 'creator' | 'host' | 'helper' | 'none'; error: any } | null>(null)
  const [rawOpen, setRawOpen] = useState(false)
  const [rawData, setRawData] = useState<any | null>(null)

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const perms = await Event.getUserEventPermissions(eventId, asUserId)
        setPermissions(perms)
        if (!perms.canView) {
          setError('No permissions to view this event')
          return
        }
        const { data } = await Event.getById(eventId)
        setEventData(data)
        if (perms.role === 'helper') setActiveTab('guests')
        else if (perms.canManageAttendees) setActiveTab('attendance')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [eventId, asUserId])

  const toggleRaw = async (open: boolean) => {
    setRawOpen(open)
    if (open && !rawData) {
      const { data } = await Event.getRaw(eventId)
      setRawData(data)
    }
  }

  const transformed = useMemo(() => {
    if (!eventData) return null
    return {
      id: eventData.id,
      title: eventData.title,
      description: eventData.description || eventData.short_description || '',
      date: `${eventData.start_date}T${eventData.start_time || '00:00:00'}Z`,
      startDate: eventData.start_date || '00',
      endDate: eventData.end_date || '00',
      startTime: eventData.start_time || '00:00:00',
      endTime: eventData.end_time || '00:00:00',
      location: eventData.location || 'TBD',
      imageUrl: eventData.image_url || undefined,
      organizer: { id: '', name: 'Event Organizer', email: '' },
      limited_places: eventData.limited_places || 0,
      coHosts: (eventData.hosts || []).filter((h: any) => !h.is_main_host).map((h: any) => ({
        id: h.id,
        name: h.name || h.email?.split('@')[0],
        email: h.email,
        avatar: h.avatar_url || undefined,
        status: h.status || 'pending',
        invited_at: h.invited_at || new Date().toISOString()
      })),
      guests: [],
      waitlist: [],
      parent_event_id: eventData.parent_event_id ?? '',
      type: eventData.type,
      category: eventData.category || undefined,
      modality: eventData.modality || undefined,
    }
  }, [eventData])

  if (loading) return <div className="text-sm text-gray-500">Loading event…</div>
  if (error) return <div className="text-sm text-red-600">{error}</div>
  if (!permissions?.canView || !transformed) return null

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <EventCard
            eventData={transformed}
            canEdit={permissions.canEdit}
            canDelete={permissions.role === 'creator'}
          />
        </div>
        <div className="pt-2">
          <Popover open={rawOpen} onOpenChange={toggleRaw}>
            <PopoverTrigger asChild>
              <Button size="sm" variant="outline">Raw data</Button>
            </PopoverTrigger>
            <PopoverContent className="p-2 w-[36rem] max-w-[90vw]">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-medium">Raw event</div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" onClick={() => {
                    const text = JSON.stringify(rawData ?? {}, null, 2)
                    navigator.clipboard.writeText(text)
                  }}>Copy</Button>
                  <Button size="sm" variant="ghost" onClick={() => window.open(`/api/admin/event/${eventId}`,'_blank')}>Open</Button>
                </div>
              </div>
              <pre className="text-[11px] whitespace-pre-wrap break-words max-h-80 overflow-auto bg-gray-50 p-2 rounded">
{JSON.stringify(rawData ?? {}, null, 2)}
              </pre>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)} defaultValue="guests">
        <div className="border-b mb-4">
          <TabsList className="bg-transparent p-0 w-full justify-start">
            <div className="flex overflow-x-auto pb-1 min-w-full">
              <TabsTrigger value="guests" className="bg-transparent border-0 rounded-none px-3 py-2 border-b-2 border-transparent data-[state=active]:border-[#00B383] hover:text-[#00B383] text-sm whitespace-nowrap flex-shrink-0">Guests</TabsTrigger>
              {permissions.canManageAttendees && (
                <TabsTrigger value="cohosts" className="bg-transparent border-0 rounded-none px-3 py-2 border-b-2 border-transparent data-[state=active]:border-[#00B383] hover:text-[#00B383] text-sm whitespace-nowrap flex-shrink-0">Co-hosts</TabsTrigger>
              )}
              {permissions.canManageAttendees && (
                <TabsTrigger value="attendance" className="bg-transparent border-0 rounded-none px-3 py-2 border-b-2 border-transparent data-[state=active]:border-[#00B383] hover:text-[#00B383] text-sm whitespace-nowrap flex-shrink-0">Helpers</TabsTrigger>
              )}
              {permissions.canManageAttendees && (
                <TabsTrigger value="communication" className="bg-transparent border-0 rounded-none px-3 py-2 border-b-2 border-transparent data-[state=active]:border-[#00B383] hover:text-[#00B383] text-sm whitespace-nowrap flex-shrink-0">Communication</TabsTrigger>
              )}
            </div>
          </TabsList>
        </div>

        <div className="min-h-0 overflow-hidden">
          <TabsContent value="guests" className="mt-0">
            <GuestList eventId={transformed.id} guestLimit={transformed.limited_places} />
          </TabsContent>
          <TabsContent value="cohosts" className="mt-0">
            {permissions.canManageAttendees && (
              <CoHostsManager eventId={transformed.id} coHosts={transformed.coHosts} onUpdate={() => undefined} />
            )}
          </TabsContent>
          <TabsContent value="attendance" className="mt-0">
            {permissions.canManageAttendees && (
              <AttendanceManager eventId={transformed.id} requiresApproval={false} />
            )}
          </TabsContent>
          <TabsContent value="communication" className="mt-0">
            {permissions.canManageAttendees && (
              <CommunicationPanel eventId={transformed.id} eventTitle={transformed.title} />
            )}
          </TabsContent>
        </div>
      </Tabs>
    </div>
  )
}


